import { useTheme } from '@mui/material';
import { useId, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { stripSearchAndHash } from '../../utils/strip-search-and-hash';
import { Badge } from '../Badge';
import { ElementWithBadge } from '../ElementWithBadge';
import { ChevronDownIcon } from '../Icons/ChevronDownIcon';
import {
  useSideNavExpanded,
  useSideNavLinkClick,
} from '../SideNavbar/SideNavContext';
import {
  GroupChevron,
  GroupHeader,
  ItemWrapper,
  LabelContainer,
  StyledBox,
  StyledNavItemLabel,
  SubmenuCollapse,
  SubmenuContainer,
  SubmenuLink,
} from './NavItem.styles';
import { NavItemGroupProps } from './types';
import { getNavIconColors } from './utils';

export function NavItemGroup({
  label,
  icon,
  activePathPrefix,
  activeColor,
  activeSecondaryColor,
  iconBadge,
  iconBadgeTestId,
  subItems,
}: NavItemGroupProps) {
  const theme = useTheme();
  const expanded = useSideNavExpanded();
  const { pathname } = useLocation();
  const submenuId = useId();
  const onLinkClick = useSideNavLinkClick();

  const visibleSubItems = subItems.filter(({ visible = true }) => visible);
  const isGroupActive = activePathPrefix
    ? pathname.startsWith(activePathPrefix)
    : visibleSubItems.some((item) =>
        pathname.startsWith(stripSearchAndHash(item.to)),
      );

  const [open, setOpen] = useState(true);
  const [prevExpanded, setPrevExpanded] = useState(expanded);
  const [prevGroupActive, setPrevGroupActive] = useState(isGroupActive);

  if (expanded !== prevExpanded) {
    setPrevExpanded(expanded);
    if (expanded) {
      setOpen(true);
    }
  }

  if (isGroupActive !== prevGroupActive) {
    setPrevGroupActive(isGroupActive);
    if (isGroupActive) {
      setOpen(true);
    }
  }

  if (!visibleSubItems.length) {
    return null;
  }

  const submenuVisible = expanded && open;
  const toggleOpen = () => setOpen((current) => !current);

  const headerIcon = iconBadge ? (
    <ElementWithBadge
      badgeColor={iconBadge}
      counter={0}
      data-testid={iconBadgeTestId}
      element={icon}
      name={label ?? ''}
      size="small"
    />
  ) : (
    icon
  );
  const labelColor = isGroupActive
    ? theme.palette.text.primaryInverted
    : theme.palette.text.secondaryInverted;
  const { color, primaryColor, secondaryColor } = getNavIconColors({
    theme,
    active: isGroupActive,
    labelColor,
    activeColor,
    activeSecondaryColor,
  });
  const headerLabel = label ? (
    <LabelContainer aria-hidden={!expanded} expanded={expanded}>
      <StyledNavItemLabel variant="bodyStrong">{label}</StyledNavItemLabel>
    </LabelContainer>
  ) : null;
  const headerContent = (
    <ItemWrapper color={labelColor}>
      <StyledBox
        color={color}
        expanded={expanded}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      >
        {headerIcon}
      </StyledBox>
      {headerLabel}
      <GroupChevron aria-hidden expanded={expanded} open={open}>
        <ChevronDownIcon height={16} width={16} />
      </GroupChevron>
    </ItemWrapper>
  );
  const submenuLinks = visibleSubItems.map((item) => (
    <SubmenuLink end key={item.label} onClick={onLinkClick} to={item.to}>
      <StyledNavItemLabel variant="bodyStrong">{item.label}</StyledNavItemLabel>
      {item.badge ? (
        <Badge
          color={item.badge}
          count={0}
          data-testid={item.badgeTestId}
          size="small"
        />
      ) : null}
    </SubmenuLink>
  ));

  return (
    <div aria-label={label} role="group">
      <GroupHeader
        aria-controls={submenuId}
        aria-expanded={submenuVisible}
        aria-label={label}
        onClick={toggleOpen}
        type="button"
      >
        {headerContent}
      </GroupHeader>
      <SubmenuCollapse
        aria-hidden={!submenuVisible}
        expanded={submenuVisible}
        id={submenuId}
        inert={!submenuVisible}
      >
        <SubmenuContainer>{submenuLinks}</SubmenuContainer>
      </SubmenuCollapse>
    </div>
  );
}
